import { useTranslation } from "react-i18next";

import SettingsTypeWrapper from "@/components/DetailsSection/components/DetailsContent/components/Settings/SettingsTypeWrapper/SettingsTypeWrapper";

import { CompanySettingCategory, INPUT_LENGTH } from "../..";
import PaperBox from "../../../components/PaperBox/PaperBox";
import ToggleItemWithTooltip from "../../../components/ToggleItem/ToggleItemWithTooltip";
import { GenerateCompanySettingsPropsType } from "../../../Pages/Companies/types";

const useCompaniesFormInputs = ({ ...props }: any = {}) => {
  const { t } = useTranslation();

  const {
    setNameInputError,
    setName,
    setDescriptionInputError,
    setDescription
  } = props;

  const isNameValid = (name: string) => {
    const companyName = name.trim();

    return (
      name &&
      companyName.length <= INPUT_LENGTH.CompanyName &&
      companyName.length >= INPUT_LENGTH.MinCompanyName
    );
  };

  const isDescriptionValid = (description: string | null) =>
    (description && description.length <= INPUT_LENGTH.Description) ||
    !description;

  const validateNameLength = (e: any) => {
    const inputValue = e.target.value.trim();
    if (inputValue.length < INPUT_LENGTH.MinCompanyName) {
      setNameInputError(
        t("Dialog##name too short", {
          minNumber: INPUT_LENGTH.MinCompanyName
        })
      );
    }
    if (inputValue.length > INPUT_LENGTH.CompanyName) {
      setNameInputError(
        t("Dialog##name not valid", {
          maxNumber: INPUT_LENGTH.CompanyName
        })
      );
    }
    if (inputValue.length === 0) {
      setNameInputError(t("Dialog##name should not be empty"));
    }
  };

  const validateDescriptionLength = (e: any) => {
    const inputValue = e.target.value.trim();
    if (inputValue.length > INPUT_LENGTH.Description) {
      setDescriptionInputError(
        t("Dialog##description not valid", {
          maxNumber: INPUT_LENGTH.Description
        })
      );
    }
  };

  const handleNameChangeValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setName(e.target.value);
    validateNameLength(e);
    setNameInputError(null);
  };

  const handleChangeDescription = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setDescription(e.target.value);
    validateDescriptionLength(e);
    setDescriptionInputError(null);
  };

  const resetAllValues = () => {
    setName("");
    setDescription("");
    setNameInputError(null);
    setDescriptionInputError(null);
  };

  const createCompanySettingsConfig = ({
    assignVehicleRegistrationNumber,
    handleAutoAssignVehicleRegistrationNumber,
    autoInitialVehicleDownload,
    handleToggleAutoInitialVehicleDownload,
    assignGlobalScheduleToVehicle,
    handleToggleAssignGlobalScheduleToVehicle,
    addNewDriver,
    handleToggleAddNewDriver,
    autoDriverFileDownload,
    handleToggleAutoDriverFileDownload,
    assignGlobalScheduleToDriver,
    handleToggleAssignGlobalScheduleToDriver
  }: GenerateCompanySettingsPropsType): CompanySettingCategory[] => [
    {
      type: t("Details##vehicles"),
      settings: [
        {
          title: t(
            "Dialog##company settings##auto update vehicle registration number"
          ),
          tooltipText: t(
            "Dialog##company settings##auto update vehicle registration number tooltip"
          ),
          isChecked: assignVehicleRegistrationNumber,
          action: handleAutoAssignVehicleRegistrationNumber
        },

        {
          title: t(
            "Dialog##company settings##automatic initial vehicle download"
          ),
          tooltipText: t(
            "Dialog##company settings##automatic initial vehicle download tooltip"
          ),
          isChecked: autoInitialVehicleDownload,
          action: handleToggleAutoInitialVehicleDownload
        },

        {
          title: t(
            "Dialog##company settings##auto assign global schedule to vehicles"
          ),
          tooltipText: t(
            "Dialog##company settings##auto assign global schedule to vehicles tooltip"
          ),
          isChecked: assignGlobalScheduleToVehicle,
          action: handleToggleAssignGlobalScheduleToVehicle
        }
      ],
      customContainerStyle: { paddingBottom: "8px" }
    },
    {
      type: t("Details##drivers"),
      settings: [
        {
          title: t("Dialog##company settings##auto add new drivers"),
          tooltipText: t(
            "Dialog##company settings##auto add new drivers tooltip"
          ),
          isChecked: addNewDriver,
          action: handleToggleAddNewDriver
        },
        {
          title: t("Dialog##company settings##auto download driver data"),
          tooltipText: t(
            "Dialog##company settings##auto download driver data tooltip"
          ),
          isChecked: autoDriverFileDownload,
          action: handleToggleAutoDriverFileDownload
        },
        {
          title: t(
            "Dialog##company settings##auto assign global schedule to drivers"
          ),
          tooltipText: t(
            "Dialog##company settings##auto assign global schedule to drivers tooltip"
          ),
          isChecked: assignGlobalScheduleToDriver,
          action: handleToggleAssignGlobalScheduleToDriver
        }
      ]
    }
  ];

  const generateCompanySettings = (props: GenerateCompanySettingsPropsType) => {
    const settingsConfig = createCompanySettingsConfig(props);

    const { isDisabled, readOnly } = props;

    return (
      <PaperBox
        boxTitle={t("Dialog##company settings##title")}
        customStyling={{ padding: "0 16px" }}
      >
        {settingsConfig.map(category => (
          <SettingsTypeWrapper key={category.type} type={category.type}>
            {category.settings.map((setting, index) => (
              <ToggleItemWithTooltip
                key={setting.title}
                isDisabled={isDisabled}
                totalItemsCount={category.settings.length}
                index={index}
                title={setting.title}
                tooltipText={setting.tooltipText}
                onToggle={setting.action}
                isChecked={setting.isChecked}
                testId={setting.title}
                readOnly={readOnly}
                customContainerStyle={category.customContainerStyle}
              />
            ))}
          </SettingsTypeWrapper>
        ))}
      </PaperBox>
    );
  };

  return {
    validateNameLength,
    handleNameChangeValue,
    resetAllValues,
    validateDescriptionLength,
    handleChangeDescription,
    isNameValid,
    isDescriptionValid,
    generateCompanySettings
  };
};

export default useCompaniesFormInputs;
